import * as yup from 'yup';
import { useFormik } from 'formik';
import { PlansBillingController } from '../../../controllers';
import constant from '../../../utils/constant';
import { MimaToastUtil } from '../../../components';
import { useMemo } from 'react';
import { refreshQuery, removeEmptyKeys } from '../../../utils/utils';

export const useCreatePlanModal = ({ closeModal, currentPlan }) => {
	const initialValues = useMemo(() => {
		return {
			name: currentPlan?.name ?? '',
			interval: currentPlan?.interval ?? 'monthly',
			cycle: currentPlan?.cycle ?? 0,
			currencyCode: currentPlan?.currencyCode ?? 'NGN',
			amount: currentPlan?.amount ?? 0,
			trialPeriod: currentPlan?.trialPeriod ?? 0,
			id: currentPlan?._id || '',
			activateUrl: currentPlan?.activateUrl || '',
			deActivateUrl: currentPlan?.deActivateUrl || '',
		};
	}, [currentPlan]);
	const validationSchema = yup.object().shape({
		name: yup.string().required('Plan name is required'),
		interval: yup.string().required('Plan interval is required'),
		cycle: yup.number().optional(),
		currencyCode: yup
			.string()
			.length(3, 'Currency code must be 3 characters')
			.required('Currency code is required'),
		amount: yup
			.number()
			.positive('Amount must be greater than 0')
			.required('Amount is required'),
		trialPeriod: yup
			.number()
			.typeError('Trial period must be a number')
			.min(0, 'Trial period cannot be negative')
			.integer('Trial period must be a whole number'),
	});

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: validationSchema,
		onSubmit: async values => {
			const payload = { ...values };

			if (values.cycle <= 0) {
				delete payload.cycle;
			}
			delete payload.id;

			removeEmptyKeys(payload);

			const { status, errorMessage } = values?.id
				? await PlansBillingController.updatePlan(payload, values.id)
				: await PlansBillingController.createPlan(payload);
			if (status === constant.Success) {
				MimaToastUtil.success({
					message: constant.Success,
				});
				await refreshQuery({
					queryKey: [constant.apiQueryKey.subscriptionPlans],
				});
				closeModal();
				return;
			}
			return MimaToastUtil.error({
				message: errorMessage,
			});
		},
	});

	const intervals = [
		{ value: 'Daily', key: 'daily' },
		{ value: 'Weekly', key: 'weekly' },
		{ value: 'Monthly', key: 'monthly' },
		{ value: 'Quarterly', key: 'quarterly' },
		{ value: 'Bianually', key: 'biannually' },
		{ value: 'Yearly', key: 'yearly' },
	];

	const getCycleLabel = interval => {
		switch (interval) {
			case 'daily':
				return 'How many days should this plan run?';
			case 'weekly':
				return 'How many weeks should this plan run?';
			case 'monthly':
				return 'How many months should this plan run?';
			case 'quarterly':
				return 'How many quarters should this plan run?';
			case 'yearly':
				return 'How many years should this plan run?';
			default:
				return 'How many times should this plan run?';
		}
	};

	const currencyOptions = [
		{
			name: 'Nigerian Naira',
			symbol: '₦',
			symbolNative: '₦',
			decimalDigits: 2,
			rounding: 0,
			code: 'NGN',
			namePlural: 'Nigerian nairas',
		},
		{
			name: 'US Dollar',
			symbol: '$',
			symbolNative: '$',
			decimalDigits: 2,
			rounding: 0,
			code: 'USD',
			namePlural: 'US dollars',
		},
	].map(x => ({
		value: x.name,
		key: x.code,
	}));

	return { formik, intervals, getCycleLabel, currencyOptions };
};

export default useCreatePlanModal;
